<template>
  <div class="modal-overlay planning">
    <div class="modal-content">
      <div class="modal-header">
        <h3><span v-if="searchLoading">Recherche en cours...</span><span v-else>Créer un nouveau planning</span></h3>
        <button class="close-btn" @click="close">&times;</button>
      </div>

      <div class="modal-body">
        <form @submit.prevent="submitForm">

          <!-- Centres liés -->
          <div v-if="hasMultipleOfs" class="form-group">
            <div class="form-check">
              <input
                  id="toggleLinkedOf"
                  v-model="isLinkOtherOfEnabled"
                  class="form-check-input"
                  type="checkbox"
              />
              <label class="form-check-label form-label" for="toggleLinkedOf">Gérer les candidatures des centres de :</label>
            </div>

            <div v-if="isLinkOtherOfEnabled">
              <div v-if="selectedOfObjects.length > 0" class="pills-container">
                <span
                    v-for="of in selectedOfObjects"
                    :key="of.ofObjId"
                    class="pill"
                >
                  {{ of.ofName }}
                  <button type="button" class="pill-remove" @click="removeOf(of.ofObjId)">&times;</button>
                </span>
              </div>

              <select
                  id="linkedOf"
                  v-model="selectedOf"
                  class="form-control"
                  @change="addOf"
              >
                <option
                    v-for="of in filteredOfList"
                    :key="of.ofObjId"
                    :value="of.ofObjId"
                >
                  {{ of.ofName }}
                </option>
              </select>
            </div>
          </div>

          <!-- Sélectionner une adresse d'entretien -->
          <div v-if="formData.ofsIdsEffective.length > 0" class="form-group">
            <label class="form-label" for="meetingPlace">Sélectionner une adresse d'entretien :</label>
            <select
                id="meetingPlace"
                v-model="formData.ofIdMeetingPlace"
                class="form-control"
                required
            >
              <option
                  v-for="option in meetingPlaceOptions"
                  :key="option.ofObjId"
                  :value="option.ofObjId"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <!-- Date & heure de début -->
          <div class="form-group">
            <label for="startAt" class="form-label">Date et heure de début :</label>
            <VueDatePicker
                v-model="formData.startAt"
                :enable-time-picker="true"
                :is24="true"
                :max-time="{ hours: 19, minutes: 0 }"
                :min-time="{ hours: 7, minutes: 0 }"
                :minute-increment="5"
                :start-time="{ hours: 9, minutes: 0 }"
                :time-picker-inline="true"
                cancel-text="Annuler"
                format="yyyy-MM-dd HH:mm"
                locale="fr"
                placeholder="Sélectionnez la date et l'heure de début"
                select-text="Valider"
                required
            />
          </div>

          <!-- Sélection du type d'entretien -->
          <div v-if="formData.startAt" class="form-group">
            <label class="form-label">Type de convocations :</label>
            <div class="workflow-selector">
              <button
                  type="button"
                  class="workflow-btn"
                  :class="{ active: workflowType === 'individual' }"
                  @click="setWorkflow('individual')"
              >
                <span class="workflow-icon"><img :src="unique" style="width: 120px"></span>
                <span class="workflow-label">Créneau horaire défini par candidat</span>
              </button>
              <button
                  type="button"
                  class="workflow-btn"
                  :class="{ active: workflowType === 'collective' }"
                  @click="setWorkflow('collective')"
              >
                <span class="workflow-icon"><img :src="multi" style="width: 120px"></span>
                <span class="workflow-label">Plage horaire commune pour un ensemble de candidats</span>
              </button>
            </div>
          </div>

          <!-- Workflow individuel : Durée de session -->
          <div v-if="formData.startAt && workflowType === 'individual'" class="form-group">
            <label for="sessionDuration">Durée des entretiens (minutes) :</label>
            <select
                id="sessionDuration"
                v-model.number="formData.sessionDuration"
                class="form-control"
                required
            >
              <option disabled value="">Sélectionnez une durée</option>
              <option
                  v-for="duration in ALLOWED_SESSION_DURATIONS"
                  :key="duration"
                  :value="duration"
              >
                {{ duration }} minutes
              </option>
            </select>
          </div>

          <!-- Workflow individuel : Heure de fin calculée -->
          <div v-if="formData.startAt && workflowType === 'individual' && formData.sessionDuration" class="form-group">
            <label for="endTime">Heure de fin :</label>
            <select
                id="endTime"
                v-model="selectedEndTime"
                class="form-control"
                required
            >
              <option disabled value="">Sélectionnez une heure de fin</option>
              <option
                  v-for="time in availableEndTimes"
                  :key="time.value"
                  :value="time.value"
              >
                {{ time.label }}
              </option>
            </select>
          </div>

          <!-- Workflow collectif : Nombre de candidats -->
          <div v-if="formData.startAt && workflowType === 'collective'" class="form-group">
            <label for="candidacyGroupCount">Nombre de candidats :</label>
            <select
                id="candidacyGroupCount"
                v-model.number="formData.candidacyGroupCount"
                class="form-control"
                required
            >
              <option v-for="n in 30" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>

          <!-- Workflow collectif : Heure de fin libre -->
          <div v-if="formData.startAt && workflowType === 'collective' && formData.candidacyGroupCount > 0" class="form-group">
            <label for="endTimeFree">Heure de fin :</label>
            <select
                id="endTimeFree"
                v-model="collectiveEndTime"
                class="form-control"
                required
            >
              <option disabled value="">Sélectionnez une heure de fin</option>
              <option
                  v-for="time in availableCollectiveEndTimes"
                  :key="time.value"
                  :value="time.value"
              >
                {{ time.label }}
              </option>
            </select>
          </div>

          <!-- Recherche de responsable -->
          <div class="form-group">
            <SearchInput
                v-model="searchTerm"
                :clear-button-title="UI_MESSAGES.CLEAR_BUTTON_TITLE"
                :has-value="!!formData.interviewManagerObjId"
                :loading="searchLoading"
                :help-text="UI_MESSAGES.HELP_TEXT_LEAVE_EMPTY"
                :label="'Entretiens réalisés par :'"
                :placeholder="UI_MESSAGES.SEARCH_MANAGER_PLACEHOLDER"
                :suggestion-key-field="'interviewManagerObjId'"
                :suggestions="suggestions"
                @clear="clearSelection"
                @search="onSearch"
                @select="selectInterviewManager"
                @clear-suggestions="clearSuggestions"
            >
              <template #suggestion="{ suggestion }">
                {{ formatPersonDisplayName(suggestion) }}
                <small v-if="suggestion.email"> — {{ suggestion.email }}</small>
              </template>
            </SearchInput>
          </div>



          <div v-if="workflowType !== ''" class="form-group">
            <div class="visibility-selector">
              <button
                  type="button"
                  class="visibility-btn"
                  :class="{ active: !formData.public }"
                  @click="setMeetingVisibility(false)"
              >
                <span class="visibility-chip">🔒 Privé</span>
                <span class="visibility-title">Visible uniquement en interne</span>
                <span class="visibility-description">Les créneaux restent masqués côté candidat.</span>
              </button>
              <button
                  type="button"
                  class="visibility-btn"
                  :class="{ active: formData.public }"
                  @click="setMeetingVisibility(true)"
              >
                <span class="visibility-chip">🌐 Public</span>
                <span class="visibility-title">Visible pour les candidats</span>
                <span class="visibility-description">Les candidats peuvent voir les créneaux et se positionner.</span>
              </button>
            </div>
            <small class="visibility-help">Vous pourrez modifier cette visibilité plus tard, créneau par créneau.</small>
          </div>

          <div v-if="workflowType !== ''" class="form-group">
            <div class="visibility-selector">
              <button
                  type="button"
                  class="visibility-btn"
                  :class="{ active: !formData.remote }"
                  @click="setMeetingFormat(false)"
              >
                <span class="visibility-chip">📍 Présentiel</span>
                <span class="visibility-title">Rendez-vous dans vos locaux</span>
                <span class="visibility-description">Les entretiens se déroulent en face à face sur site.</span>
              </button>
              <button
                  type="button"
                  class="visibility-btn"
                  :class="{ active: formData.remote }"
                  @click="setMeetingFormat(true)"
              >
                <span class="visibility-chip">
                  <svg style="width:1em;height:1em;vertical-align:-0.15em;margin-right:0.3em;" viewBox="0 0 16 16" aria-hidden="true"><path d="M0 5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1.5l3.553-2.22A.5.5 0 0 1 16 4.704v6.592a.5.5 0 0 1-.447.496L12 9.573V11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2z"/></svg>Distanciel
                </span>
                <span class="visibility-title">Rendez-vous en visioconférence</span>
                <span class="visibility-description">Les entretiens se déroulent à distance, par visio.</span>
              </button>
            </div>
            <small class="visibility-help">Vous pourrez modifier les modalités du rendez-vous plus tard, créneau par créneau.</small>
          </div>

          <!-- Suivre par email -->
          <div v-if="workflowType !== ''" class="form-group">
            <label class="form-label">
              {{ formData.public
                ? "Suivre par email l'avancement des convocations et le positionnement des candidats sur les créneaux publics :"
                : "Suivre par email l'avancement des convocations :" }}
            </label>
            <div class="form-check">
              <input
                  id="notificationOwner"
                  v-model="formData.notificationOwner"
                  class="form-check-input"
                  type="checkbox"
              />
              <label class="form-label form-check-label" for="notificationOwner">{{ authVars.firstname }} {{ authVars.lastname }}<small>— {{ authVars.email }}</small></label>
            </div>
            <div v-if="selectedInterviewManagerName" class="form-check">
              <input
                  id="notificationInterviewManager"
                  v-model="formData.notificationInterviewManager"
                  class="form-check-input"
                  type="checkbox"
              />
              <label class="form-label form-check-label" for="notificationInterviewManager">{{ selectedInterviewManagerName }}<small v-if="selectedInterviewManagerEmail">— {{ selectedInterviewManagerEmail }}</small></label>
            </div>
          </div>

          <div v-if="error" class="alert alert-danger">
            {{ error }}
          </div>
          <!-- Boutons -->
          <div class="form-actions">
            <button class="btn btn-secondary" type="button" @click="close">
              Annuler
            </button>
            <button :disabled="loading || workflowType === ''" class="btn btn-primary" type="submit">
              {{ loading ? 'Création...' : 'Créer le planning' }}
            </button>
          </div>
        </form>

      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, reactive, ref, watch} from 'vue'
import {useRoute} from 'vue-router'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import {storeToRefs} from 'pinia'
import {usePlanningStore} from '@/store/planningStore'
import {useDateFormatter} from '@/composables/useDateFormatter'
import {formatPersonDisplayName, useSearchAutocomplete} from '@/composables/useSearchAutocomplete'
import SearchInput from '@/components/UI/SearchInput.vue'
import {ALLOWED_SESSION_DURATIONS, SEARCH_CONFIG, TIME_CONSTRAINTS, UI_MESSAGES} from '@/constants/planning'

import unique from '@/assets/img/unique.png'
import multi from '@/assets/img/multi.png'

const emit = defineEmits(['close', 'created'])
const props = defineProps({
  ofObjId: {type: Number, required: true},
  userAccountObjId: {type: Number, required: true}
})

const route = useRoute()
const planningStore = usePlanningStore()
const {authVars} = storeToRefs(planningStore)
const {formatDateForAPI} = useDateFormatter()
const loading = ref(false)
const error = ref(null)
const selectedEndTime = ref('')
const workflowType = ref('')

const formData = reactive({
  startAt: null,
  endAt: null,
  sessionDuration: '',
  candidacyGroupCount: 0,
  interviewManagerObjId: null,
  ofsIdsEffective: [],
  ofIdMeetingPlace: null,
  notificationOwner: true,
  notificationInterviewManager: true,
  public: false,
  remote: false
})

const selectedOf = ref('')
const selectedInterviewManagerName = ref(null)
const selectedInterviewManagerEmail = ref(null)
const isLinkOtherOfEnabled = ref(false)

const ofList = computed(() => planningStore.ofList)

const hasMultipleOfs = computed(() => ofList.value.length > 1)

const meetingPlaceOptions = computed(() => {
  if (formData.ofsIdsEffective.length === 0) return []

  const options = []

  // // Ajouter le centre actuel (props.ofObjId)
  // const currentOf = ofList.value.find(of => of.ofObjId === props.ofObjId)
  // if (currentOf) {
  //   options.push({
  //     ofObjId: currentOf.ofObjId,
  //     label: `${currentOf.ofName} - ${currentOf.campusAddress}, ${currentOf.campusPostalCode} ${currentOf.campusCityName}`
  //   })
  // }

  // Ajouter les centres liés
  selectedOfObjects.value.forEach(of => {
    options.push({
      ofObjId: of.ofObjId,
      label: `${of.ofName} - ${of.campusAddress}, ${of.campusPostalCode} ${of.campusCityName}`
    })
  })

  return options
})

const filteredOfList = computed(() => {
  return ofList.value.filter(of => {
    return !formData.ofsIdsEffective.includes(of.ofObjId)
  })
})

const selectedOfObjects = computed(() => {
  return ofList.value.filter(of => formData.ofsIdsEffective.includes(of.ofObjId))
})

onMounted(() => {
  planningStore.fetchOfList(props.ofObjId, props.userAccountObjId)
})

watch(isLinkOtherOfEnabled, (enabled) => {
  if (enabled) {
    if (!formData.ofsIdsEffective.includes(props.ofObjId)) {
      formData.ofsIdsEffective.push(props.ofObjId)
    }
    return
  }

  selectedOf.value = ''
  formData.ofsIdsEffective = []
  formData.ofIdMeetingPlace = null
})



const addOf = () => {
  if (selectedOf.value && !formData.ofsIdsEffective.includes(selectedOf.value)) {
    formData.ofsIdsEffective.push(selectedOf.value)
    selectedOf.value = ''
  }
}

const removeOf = (ofObjId) => {
  formData.ofsIdsEffective = formData.ofsIdsEffective.filter(id => id !== ofObjId)
  if (formData.ofIdMeetingPlace === ofObjId) {
    formData.ofIdMeetingPlace = null
  }
  clearSelection()
}

const availableEndTimes = computed(() => {
  if (!formData.startAt || !formData.sessionDuration) return []
  const startDate = new Date(formData.startAt)
  const totalMinutes = formData.sessionDuration
  const endTimes = []
  let currentTime = new Date(startDate.getTime() + totalMinutes * 60000)

  // Continue jusqu'à atteindre l'heure limite maximum
  while (
      currentTime.getHours() < TIME_CONSTRAINTS.MAX_HOUR ||
      (currentTime.getHours() === TIME_CONSTRAINTS.MAX_HOUR && currentTime.getMinutes() === TIME_CONSTRAINTS.MAX_MINUTE)
      ) {
    // Vérifie si la durée totale est un multiple de sessionDuration
    const durationInMinutes = (currentTime - startDate) / 1000 / 60
    if (durationInMinutes % formData.sessionDuration === 0) {
      endTimes.push({
        value: currentTime.toISOString(),
        label: `${String(currentTime.getHours()).padStart(2, '0')}:${String(
            currentTime.getMinutes()
        ).padStart(2, '0')}`
      })
    }
    currentTime = new Date(currentTime.getTime() + 5 * 60000) // Incrément de 5 minutes
  }
  return endTimes
})

const collectiveEndTime = ref('')

const availableCollectiveEndTimes = computed(() => {
  if (!formData.startAt) return []
  const startDate = new Date(formData.startAt)
  const endTimes = []

  // Commence 5 minutes après l'heure de début
  let currentTime = new Date(startDate.getTime() + 5 * 60000)

  // Continue jusqu'à atteindre l'heure limite maximum (19h00)
  while (
      currentTime.getHours() < TIME_CONSTRAINTS.MAX_HOUR ||
      (currentTime.getHours() === TIME_CONSTRAINTS.MAX_HOUR && currentTime.getMinutes() === TIME_CONSTRAINTS.MAX_MINUTE)
  ) {
    endTimes.push({
      value: currentTime.toISOString(),
      label: `${String(currentTime.getHours()).padStart(2, '0')}:${String(
          currentTime.getMinutes()
      ).padStart(2, '0')}`
    })
    currentTime = new Date(currentTime.getTime() + 5 * 60000) // Incrément de 5 minutes

    // Arrêter après 19h00
    if (currentTime.getHours() > TIME_CONSTRAINTS.MAX_HOUR) break
  }

  return endTimes
})

const setWorkflow = (type) => {
  workflowType.value = type
  formData.sessionDuration = ''
  formData.candidacyGroupCount = 0
  formData.endAt = null
  selectedEndTime.value = ''
  collectiveEndTime.value = ''
}

const setMeetingVisibility = (isPublic) => {
  formData.public = isPublic
}

const setMeetingFormat = (isRemote) => {
  formData.remote = isRemote
}

watch(() => formData.startAt, () => {
  workflowType.value = ''
  formData.sessionDuration = ''
  formData.candidacyGroupCount = 0
  formData.endAt = null
  selectedEndTime.value = ''
  collectiveEndTime.value = ''
})

watch(() => formData.sessionDuration, () => {
  formData.endAt = null
  selectedEndTime.value = ''
})

watch(() => selectedEndTime.value, () => {
  formData.endAt = selectedEndTime.value ? new Date(selectedEndTime.value) : null
  error.value = null
  if (formData.startAt && formData.endAt && formData.endAt <= formData.startAt) {
    error.value = "L'heure de fin doit être postérieure à l'heure de début."
    formData.endAt = null
    selectedEndTime.value = ''
  }
  if (
      formData.startAt &&
      formData.endAt &&
      formData.startAt.toDateString() !== formData.endAt.toDateString()
  ) {
    error.value = "La date de fin doit être le même jour que la date de début."
    formData.endAt = null
    selectedEndTime.value = ''
  }
})

watch(collectiveEndTime, (val) => {
  if (val) {
    formData.endAt = new Date(val)
  } else {
    formData.endAt = null
  }
})

const close = () => emit('close')


const submitForm = async () => {
  loading.value = true


  const formattedData = {
    ...formData,
    startAt: formatDateForAPI(formData.startAt),
    endAt: formatDateForAPI(formData.endAt),
    candidacyGroupCount: workflowType.value === 'collective' ? formData.candidacyGroupCount : 0,
    remote: formData.remote ? 1 : undefined
  }

  // Si aucun centre lié n'est sélectionné, on réinitialise ofIdMeetingPlace
  if (formData.ofsIdsEffective.length === 0) {
    formattedData.ofIdMeetingPlace = null
  }

  planningStore.createPlanning(props.ofObjId, props.userAccountObjId, formattedData).then(() => {
    emit('created')
    close()
  }).catch((err) => {
    error.value = err.message || err
  }).finally(() => {
    loading.value = false
  })
}

// Recherche avec autocomplete
const searchFunction = async (term) => {
  const promises = []



  // Recherche pour les centres liés
  if (formData.ofsIdsEffective.length !== 0) {
    formData.ofsIdsEffective.forEach(id => {
      promises.push(
          planningStore.searchInterviewManagers(
              id,
              route.params.userAccountObjId,
              term
          )
      )
    })
  } else {
    // Recherche pour le centre principal
    promises.push(
        planningStore.searchInterviewManagers(
            Number(route.params.ofObjId),
            route.params.userAccountObjId,
            term
        )
    )
  }

  const results = await Promise.all(promises)


  const allManagers = results.map(res => {
    if (Array.isArray(res)) {
      return res
    }
    return []
  }).flat()

  const uniqueManagers = []
  const seenIds = new Set()

  for (const manager of allManagers) {
    const id = manager.interviewManagerObjId || manager.id
    if (id && !seenIds.has(id)) {
      seenIds.add(id)
      uniqueManagers.push(manager)
    }
  }

  return uniqueManagers
}

const {
  searchTerm,
  suggestions,
  searchLoading,
  onSearch,
  clearSuggestions,
  clearSearch,
  selectSuggestion
} = useSearchAutocomplete(searchFunction, SEARCH_CONFIG.DEBOUNCE_MS_FAST, SEARCH_CONFIG.MIN_LENGTH)

const selectInterviewManager = (manager) => {
  const selected = selectSuggestion(manager)
  formData.interviewManagerObjId = selected.interviewManagerObjId
  selectedInterviewManagerName.value = formatPersonDisplayName(selected)
  selectedInterviewManagerEmail.value = selected.email || null
}

const clearSelection = () => {
  clearSearch()
  formData.interviewManagerObjId = null
  selectedInterviewManagerName.value = null
  selectedInterviewManagerEmail.value = null
  formData.notificationInterviewManager = false
}
</script>

<style scoped>
/* Styles spécifiques au composant - la plupart des styles sont maintenant dans global.css */
.planning .modal-content {
  overflow-y: visible;
}

.pills-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.pill {
  display: inline-flex;
  align-items: center;
  background-color: #e9ecef;
  color: #495057;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 500;
}

.pill-remove {
  background: none;
  border: none;
  color: #adb5bd;
  margin-left: 8px;
  font-size: 1.2rem;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}

.pill-remove:hover {
  color: #dc3545;
}

.workflow-selector {
  display: flex;
  gap: 12px;
}

.workflow-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 12px;
  border: 2px solid #dee2e6;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.workflow-btn:hover {
  border-color: #6c757d;
  background: #f8f9fa;
}

.workflow-btn.active {
  border-color: #0d6efd;
  background: #e7f1ff;
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.15);
}

.workflow-icon {
  font-size: 1.8rem;
}

.workflow-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
}

.workflow-btn.active .workflow-label {
  color: #0d6efd;
}

.visibility-selector {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.visibility-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  text-align: left;
  padding: 14px;
  border: 2px solid #d9dee3;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #f8f9fb 100%);
  cursor: pointer;
  transition: all 0.2s ease;
}

.visibility-btn:hover {
  border-color: #98a2ad;
  transform: translateY(-1px);
}

.visibility-btn.active {
  border-color: #0d6efd;
  background: linear-gradient(180deg, #f4f8ff 0%, #e9f1ff 100%);
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.12);
}

.visibility-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #2459d1;
  background: rgba(13, 110, 253, 0.12);
}

.visibility-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2933;
}

.visibility-description {
  font-size: 0.84rem;
  color: #5f6c7b;
  line-height: 1.35;
}

.visibility-help {
  display: block;
  margin-top: 8px;
  color: #4b5a69;
}

@media (max-width: 768px) {
  .visibility-selector {
    grid-template-columns: 1fr;
  }
}
</style>
